import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Bell, Mail, Monitor, BellOff, CalendarClock, CheckCheck, Clock, RefreshCw } from 'lucide-react';

function Toggle({ on, onClick, testId, label }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${on ? 'bg-primary/15 text-primary border-primary/30' : 'text-muted-foreground border-border/50 hover:bg-muted/40'}`}>
      {label}
    </button>
  );
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [feed, setFeed] = useState([]);
  const [unread, setUnread] = useState(0);
  const [digest, setDigest] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, n, dg] = await Promise.all([
        api.getNotificationPreferences(), api.getStewardNotifications(), api.getWorkDigest(),
      ]);
      setPrefs(p); setFeed(n.notifications || []); setUnread(n.unread || 0); setDigest(dg);
    } catch (e) {
      toast.error(e.message || 'Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePrefs = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try { await api.setNotificationPreferences(next); await load(); }
    catch (e) { toast.error(e.message); }
  };
  const toggleChannel = (ch) => {
    const has = (prefs.channels || []).includes(ch);
    const channels = has ? prefs.channels.filter(c => c !== ch) : [...(prefs.channels || []), ch];
    savePrefs({ channels: channels.length ? channels : ['in_product'] });
  };

  if (loading || !prefs) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="notifications-page">
      <button onClick={() => navigate('/my-work')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5" data-testid="notif-back">
        <ArrowLeft className="w-4 h-4" /> My Work
      </button>
      <h1 className="text-3xl font-bold text-foreground tracking-tight flex items-center gap-2">
        <Bell className="w-6 h-6 text-primary" /> Notifications
      </h1>
      <p className="text-sm text-muted-foreground mt-1">Quiet by default. Routine updates are bundled into your digest — you are not pinged for every change.</p>

      {/* Preferences */}
      <div className="mt-6 rounded-2xl border border-border/50 bg-card/40 p-5 space-y-4" data-testid="notif-prefs">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Digest frequency</p>
          <div className="flex gap-2">
            <Toggle on={prefs.digest_frequency === 'daily'} onClick={() => savePrefs({ digest_frequency: 'daily' })} testId="digest-daily" label="Daily" />
            <Toggle on={prefs.digest_frequency === 'weekly'} onClick={() => savePrefs({ digest_frequency: 'weekly' })} testId="digest-weekly" label="Weekly" />
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Delivery</p>
          <div className="flex gap-2">
            <Toggle on={(prefs.channels || []).includes('in_product')} onClick={() => toggleChannel('in_product')} testId="channel-in-product" label={<><Monitor className="w-4 h-4" /> In-product</>} />
            <Toggle on={(prefs.channels || []).includes('email')} onClick={() => toggleChannel('email')} testId="channel-email" label={<><Mail className="w-4 h-4" /> Email</>} />
          </div>
          {(prefs.channels || []).includes('email') && <p className="text-[11px] text-amber-300/80 mt-1">Email delivery is not yet wired in this preview; it will send once email is connected.</p>}
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-foreground flex items-center gap-2"><BellOff className="w-4 h-4 text-muted-foreground" /> Pause noncritical notifications</p>
            <p className="text-[12px] text-muted-foreground">Only critical governance alerts break through.</p>
          </div>
          <Toggle on={prefs.pause_noncritical} onClick={() => savePrefs({ pause_noncritical: !prefs.pause_noncritical })} testId="pause-noncritical" label={prefs.pause_noncritical ? 'Paused' : 'On'} />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Delegate while away</p>
          <input data-testid="delegate-input" defaultValue={prefs.delegate_to} onBlur={e => e.target.value !== prefs.delegate_to && savePrefs({ delegate_to: e.target.value })} placeholder="Name or role to receive your items" className="w-full h-9 px-3 rounded-lg bg-muted/40 border border-border/40 text-sm text-foreground outline-none focus:border-primary/40" />
        </div>
      </div>

      {/* Digest */}
      <div className="mt-6 rounded-2xl border border-border/50 bg-card/40 p-5" data-testid="notif-digest">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><CalendarClock className="w-4 h-4 text-primary" /> Your {digest?.frequency} digest</h2>
        <p className="text-[13px] text-muted-foreground mt-1">{digest?.summary}</p>
        {(digest?.overdue?.length > 0) && (
          <div className="mt-3">
            <p className="text-[11px] uppercase tracking-wider text-rose-300 mb-1">Overdue</p>
            {digest.overdue.map(i => <p key={i.action_id} className="text-[13px] text-muted-foreground">• {i.title} — was due {i.check_back_date}</p>)}
          </div>
        )}
        {(digest?.upcoming_check_backs?.length > 0) && (
          <div className="mt-3">
            <p className="text-[11px] uppercase tracking-wider text-amber-300 mb-1">Due soon</p>
            {digest.upcoming_check_backs.map(i => <p key={i.action_id} className="text-[13px] text-muted-foreground">• {i.title} — check back {i.check_back_date}</p>)}
          </div>
        )}
        {(digest?.goals_off_track?.length > 0) && (
          <div className="mt-3" data-testid="digest-goals-off-track">
            <p className="text-[11px] uppercase tracking-wider text-rose-300 mb-1">Goals off track</p>
            {digest.goals_off_track.map(g => <p key={g.objective_id} className="text-[13px] text-muted-foreground" data-testid={`digest-goal-${g.objective_id}`}>• {g.name} — {g.metric_name || 'metric'} now {g.current} vs target {g.target} ({g.percent}% · {g.status === 'off_track' ? 'off track' : 'at risk'}){g.source === 'auto' ? ' · auto' : ''}</p>)}
          </div>
        )}
        <div className="mt-4 pt-3 border-t border-border/30">
          <button onClick={async () => { setRefreshing(true); try { const r = await api.refreshAllGoals(); toast.success(`Refreshed ${r.refreshed} goal${r.refreshed === 1 ? '' : 's'} · ${(r.off_track || []).length} off track`); await load(); } catch (e) { toast.error(e.message || 'Could not refresh goals'); } finally { setRefreshing(false); } }} disabled={refreshing} data-testid="digest-refresh-goals" className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline disabled:opacity-50">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh goals from certified metrics now
          </button>
          <p className="text-[11px] text-muted-foreground mt-1">A scheduler runs this before each digest; run it on demand here. Off-track goals appear above.</p>
        </div>
      </div>

      {/* In-product feed */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">In-product ({unread} unread)</h2>
          {unread > 0 && <button onClick={async () => { await api.stewardMarkAllNotificationsRead(); await load(); }} data-testid="mark-all-read" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>}
        </div>
        <div className="rounded-2xl border border-border/50 bg-card/30 divide-y divide-border/30" data-testid="notif-feed">
          {feed.length === 0 && <p className="p-4 text-sm text-muted-foreground">No in-product notifications. This is a good thing — it means nothing urgent is waiting.</p>}
          {feed.map(n => (
            <div key={n.notification_id} className={`p-4 ${n.read || n.muted ? 'opacity-60' : ''}`} data-testid={`notif-${n.notification_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{n.title}{n.critical && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300">Critical</span>}</p>
                  <p className="text-[13px] text-muted-foreground mt-0.5">{n.body}</p>
                  <p className="text-[10px] text-muted-foreground/70 mt-1">{n.at ? new Date(n.at).toLocaleString() : ''}</p>
                </div>
                {!n.read && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={async () => { await api.stewardMarkNotificationRead(n.notification_id); await load(); }} data-testid={`notif-read-${n.notification_id}`} className="text-xs text-primary hover:underline">Read</button>
                    <button onClick={async () => { await api.snoozeNotification(n.notification_id, 3); await load(); }} data-testid={`notif-snooze-${n.notification_id}`} className="inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"><Clock className="w-3 h-3" /> Snooze</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
